import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../../../../i18n';
import { LoadingSpinner } from '../../../shared/components/LoadingSpinner';
import { adminRequest } from '../../../shared/services/api';
import { useAuth } from '../../../../context/AuthContext';
import { CrearAdminTenantModal } from '../components/CrearAdminTenantModal';

// Tab Backoffice — SUPERADMIN_PLATFORM only.
// Vista de gestion cross-tenant para staff de plataforma. Reemplaza la antigua ruta /backoffice
// (que ahora es tab dentro de /admin/backoffice). PRD-002 prompt 0566.

interface TenantRow {
  id: string;
  nombre: string;
  slug: string;
  plan: string;
  activo: boolean;
  maxUsuarios: number;
  _count?: {
    usuarios: number;
    documentos: number;
  };
}

const TH_STYLE: React.CSSProperties = {
  padding: '14px 20px',
  fontSize: 12,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.8px',
  color: '#fff',
  textAlign: 'left',
};

export function AdminBackoffice() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { isPlatformAdmin } = useAuth();
  const [tenants, setTenants] = useState<TenantRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [adminModal, setAdminModal] = useState<{ id: string; nombre: string } | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [impersonating, setImpersonating] = useState<string | null>(null);

  // Impersonation (prompt 0524) — patron Stripe/Linear.
  async function handleImpersonate(tenant: TenantRow) {
    const i = t.backoffice.impersonate;
    if (!confirm(i.confirmDesc(tenant.nombre))) return;
    setImpersonating(tenant.id);
    try {
      const res = await adminRequest<{
        data: { token: string; expiresIn: number; targetTenant: { nombre: string } };
      }>(`/tenants/${tenant.id}/impersonate`, { method: 'POST' });

      const original = localStorage.getItem('vianet-token');
      if (original) {
        sessionStorage.setItem('vianet-token-original', original);
      }
      localStorage.setItem('vianet-token', res.data.token);
      sessionStorage.setItem('vianet-impersonation-target-name', res.data.targetTenant.nombre);
      sessionStorage.removeItem('vianet_auth_user');
      window.location.assign('/');
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : i.error);
      setImpersonating(null);
    }
  }

  async function handleEliminarTenant(tenant: TenantRow) {
    const a = t.backoffice.addAdmin;
    if (!confirm(a.confirmEliminarDesc(tenant.nombre))) return;
    setDeleting(tenant.id);
    try {
      await adminRequest(`/tenants/${tenant.id}`, { method: 'DELETE' });
      await loadTenants();
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : String(err));
    } finally {
      setDeleting(null);
    }
  }

  const loadTenants = async () => {
    try {
      setLoading(true);
      const json = await adminRequest<{ data?: TenantRow[] }>('/backoffice/tenants');
      setTenants(json.data ?? []);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
      setTenants([
        {
          id: '1',
          nombre: 'VALOR·IA Demo',
          slug: 'valoria',
          plan: 'ENTERPRISE',
          activo: true,
          maxUsuarios: 50,
          _count: { usuarios: 12, documentos: 53 },
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTenants();
  }, []);

  const PLAN_COLORS: Record<string, string> = {
    BASIC: '#6B7280',
    PROFESSIONAL: '#2563EB',
    ENTERPRISE: '#7C3AED',
    PREMIUM: '#D97706',
  };

  return (
    <div style={{ padding: 24 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 24,
        }}
      >
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: '#1F3864', margin: 0 }}>
            {t.backoffice.gestionTenants}
          </h2>
          <p style={{ fontSize: 13, color: '#6B7280', marginTop: 4 }}>{t.backoffice.descripcion}</p>
        </div>
        {isPlatformAdmin ? (
          <button
            data-testid="backoffice-crear-tenant"
            style={{
              padding: '10px 20px',
              backgroundColor: '#7C3AED',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
            onClick={() => navigate('/backoffice/tenants/crear')}
          >
            + {t.backoffice.crearTenant}
          </button>
        ) : (
          <span
            style={{
              fontSize: 12,
              color: '#9CA3AF',
              fontStyle: 'italic',
              padding: '8px 12px',
              background: '#F9FAFB',
              border: '1px dashed #E5E7EB',
              borderRadius: 8,
            }}
          >
            Solo administradores de plataforma pueden crear tenants
          </span>
        )}
      </div>

      {error && (
        <div
          style={{
            marginBottom: 16,
            padding: '10px 16px',
            borderRadius: 8,
            fontSize: 13,
            backgroundColor: '#FEF3C7',
            color: '#92400E',
            border: '1px solid #FDE68A',
          }}
        >
          {t.backoffice.usingPlaceholder}
        </div>
      )}

      <div
        style={{
          backgroundColor: '#fff',
          borderRadius: 12,
          borderTop: '4px solid #7C3AED',
          boxShadow:
            '0 2px 1px -1px rgba(0,0,0,.2), 0 1px 1px 0 rgba(0,0,0,.14), 0 1px 3px 0 rgba(0,0,0,.12)',
          overflow: 'hidden',
        }}
      >
        {loading ? (
          <div style={{ padding: 32, textAlign: 'center' }}>
            <LoadingSpinner text={t.common.cargando} size="sm" />
          </div>
        ) : (
          <table style={{ width: '100%', fontSize: 14, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#581C87' }}>
                <th style={TH_STYLE}>{t.backoffice.columns.nombre}</th>
                <th style={TH_STYLE}>{t.backoffice.columns.plan}</th>
                <th style={TH_STYLE}>{t.backoffice.columns.usuarios}</th>
                <th style={TH_STYLE}>{t.backoffice.columns.docs}</th>
                <th style={TH_STYLE}>{t.backoffice.columns.estado}</th>
                <th style={{ ...TH_STYLE, textAlign: 'right' }}>{t.backoffice.columns.acciones}</th>
              </tr>
            </thead>
            <tbody>
              {tenants.map((tenant, idx) => (
                <tr
                  key={tenant.id}
                  style={{
                    borderBottom: '1px solid #E5E7EB',
                    backgroundColor: idx % 2 === 1 ? '#F2F4F6' : undefined,
                  }}
                >
                  <td style={{ padding: '14px 20px' }}>
                    <div style={{ fontWeight: 600, color: '#1F3864' }}>{tenant.nombre}</div>
                    <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 2 }}>
                      {tenant.slug}
                    </div>
                  </td>
                  <td style={{ padding: '14px 20px' }}>
                    <span
                      style={{
                        display: 'inline-block',
                        padding: '4px 12px',
                        borderRadius: 4,
                        fontSize: 12,
                        fontWeight: 600,
                        color: '#fff',
                        backgroundColor: PLAN_COLORS[tenant.plan] ?? '#6B7280',
                      }}
                    >
                      {tenant.plan}
                    </span>
                  </td>
                  <td style={{ padding: '14px 20px', color: '#374151' }}>
                    {tenant._count?.usuarios ?? 0} / {tenant.maxUsuarios}
                  </td>
                  <td style={{ padding: '14px 20px', color: '#374151' }}>
                    {tenant._count?.documentos ?? 0}
                  </td>
                  <td style={{ padding: '14px 20px' }}>
                    <span
                      style={{
                        display: 'inline-block',
                        padding: '4px 12px',
                        borderRadius: 4,
                        fontSize: 13,
                        fontWeight: 500,
                        backgroundColor: tenant.activo ? '#F0FDF4' : '#FEF2F2',
                        color: tenant.activo ? '#15803D' : '#DC2626',
                      }}
                    >
                      {tenant.activo ? t.admin.usuarios.activo : t.admin.usuarios.inactivo}
                    </span>
                  </td>
                  <td style={{ padding: '14px 20px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {isPlatformAdmin && (
                      <>
                        <button
                          data-testid={`tenant-row-${tenant.slug}-add-admin`}
                          onClick={() => setAdminModal({ id: tenant.id, nombre: tenant.nombre })}
                          style={{
                            fontSize: 13,
                            color: '#7C3AED',
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            fontFamily: 'inherit',
                            fontWeight: 600,
                            marginRight: 12,
                          }}
                        >
                          {t.backoffice.addAdmin.btn}
                        </button>
                        <button
                          data-testid={`tenant-row-${tenant.slug}-impersonate`}
                          onClick={() => handleImpersonate(tenant)}
                          disabled={impersonating === tenant.id}
                          style={{
                            fontSize: 13,
                            color: '#D97706',
                            background: 'none',
                            border: 'none',
                            cursor: impersonating === tenant.id ? 'not-allowed' : 'pointer',
                            fontFamily: 'inherit',
                            fontWeight: 600,
                            marginRight: 12,
                            opacity: impersonating === tenant.id ? 0.5 : 1,
                          }}
                        >
                          {t.backoffice.impersonate.btn}
                        </button>
                        <button
                          data-testid={`tenant-row-${tenant.slug}-delete`}
                          onClick={() => handleEliminarTenant(tenant)}
                          disabled={deleting === tenant.id}
                          style={{
                            fontSize: 13,
                            color: '#DC2626',
                            background: 'none',
                            border: 'none',
                            cursor: deleting === tenant.id ? 'not-allowed' : 'pointer',
                            fontFamily: 'inherit',
                            fontWeight: 500,
                            opacity: deleting === tenant.id ? 0.5 : 1,
                          }}
                        >
                          {t.backoffice.addAdmin.btnEliminar}
                        </button>
                      </>
                    )}
                    {!isPlatformAdmin && (
                      <span style={{ fontSize: 12, color: '#9CA3AF', fontStyle: 'italic' }}>—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {adminModal && (
        <CrearAdminTenantModal
          tenantId={adminModal.id}
          tenantNombre={adminModal.nombre}
          onClose={() => setAdminModal(null)}
          onSuccess={() => loadTenants()}
        />
      )}
    </div>
  );
}
